// =============================================================
// Theme → CSS-variable bridge.
//
// The single source of truth for colour is @perpetuum/core `tokens` (which is
// itself derived from export/theme.ts). Product code NEVER hard-codes hex — it
// reads named tokens. This module turns the core palette into the same
// `--sys-*` / `--brand-*` / `--status-*` CSS custom properties the prototype
// (styles/perpetuum-tokens.css) used, so the ported components keep their exact
// design contract while staying token-driven.
// =============================================================

import { palette, metrics, typography, safety, statusIndicator, type ThemeName } from "@perpetuum/core";

// Full CSS font stacks per the prototype spec (styles/iterum-base.css). The
// core token names the primary face ("Manrope" — the documented substitute for
// the licensed Iterum face); the web bridge wraps it in the prototype's
// fallback chain. The mono stack uses the real CSS family name "JetBrains
// Mono" (the core token "JetBrainsMono" is the RN asset name, which has no
// space). Font stacks are explicitly allowed verbatim — they are not colour.
const FONT_STACK_SANS = `"${typography.fontBody}", "Söhne", "Inter", system-ui, sans-serif`;
const FONT_STACK_MONO = `"JetBrains Mono", "SF Mono", ui-monospace, Menlo, monospace`;

/** Build the `--var: value;` block for a theme from the core palette. */
export function themeCssVars(theme: ThemeName): Record<string, string> {
  const p = palette[theme];
  const si = statusIndicator[theme];
  return {
    // surfaces
    "--sys-bg-main": p.bgMain,
    "--sys-bg-sunken": p.bgSunken,
    "--sys-bg-card": p.bgCard,
    "--sys-bg-raised": p.bgRaised,
    "--sys-bg-inset": p.bgInset,
    "--sys-border": p.border,
    "--sys-border-2": p.border2,
    "--sys-border-strong": p.borderStrong,
    "--sys-hairline": p.hairline,
    // text
    "--sys-fg-1": p.fg1,
    "--sys-fg-2": p.fg2,
    "--sys-fg-3": p.fg3,
    "--sys-fg-mute": p.fgMute,
    "--sys-fg-inv": p.fgInv,
    // brand
    "--brand-primary": p.brandPrimary,
    "--brand-primary-fill": p.brandFill,
    "--brand-primary-fill-h": p.brandFillHover,
    "--on-brand": p.onBrand,
    "--brand-wash": p.brandWash,
    "--brand-ring": p.brandFill,
    // status indicators (dots / bars)
    "--status-critical": si.critical,
    "--status-warning": si.warning,
    "--status-success": si.success,
    "--status-paused": si.paused,
    "--status-info": si.info,
    // status surfaces (chip bg + ink)
    "--status-critical-bg": p.criticalBg,
    "--status-critical-ink": p.criticalInk,
    "--status-warning-bg": p.warningBg,
    "--status-warning-ink": p.warningInk,
    "--status-success-bg": p.successBg,
    "--status-success-ink": p.successInk,
    "--status-paused-bg": p.pausedBg,
    "--status-paused-ink": p.pausedInk,
    "--status-info-bg": p.infoBg,
    "--status-info-ink": p.infoInk,
    "--sys-overlay": p.overlay,
    // safety fills (theme-independent — identical across themes)
    "--btn-go-bg": safety.goBg,
    "--btn-go-ink": safety.goInk,
    "--btn-go-bg-h": safety.goBgHover,
    "--btn-stop-bg": safety.stopBg,
    "--btn-stop-ink": safety.stopInk,
    "--btn-stop-bg-h": safety.stopBgHover,
    "--on-status": safety.onStatus,
    // glove metrics
    "--tap-min": `${metrics.tapMin}px`,
    "--tap-core": `${metrics.tapCore}px`,
    "--tap-hero": `${metrics.tapHero}px`,
    "--tap-gap": `${metrics.tapGap}px`,
    "--field-h": `${metrics.fieldH}px`,
    "--field-h-sm": `${metrics.fieldHSm}px`,
    "--field-font": `${metrics.fieldFont}px`,
    "--radius-md": `${metrics.radius.md}px`,
    "--radius-lg": `${metrics.radius.lg}px`,
    // type — full stacks (prototype: --font-body/--font-display/--font-mono)
    "--font-body": FONT_STACK_SANS,
    "--font-display": FONT_STACK_SANS,
    "--font-mono": FONT_STACK_MONO,
  };
}

/** Apply a theme's CSS vars to the document root (also sets data-theme). */
export function applyTheme(theme: ThemeName, root: HTMLElement = document.documentElement): void {
  root.setAttribute("data-theme", theme);
  const vars = themeCssVars(theme);
  for (const [k, v] of Object.entries(vars)) root.style.setProperty(k, v);
}
